import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CartIcon } from '@bi-digital/icons';
import type { ButtonProps } from '@bi-digital/react';
import { Button } from '@bi-digital/react';
import { trackGAEventForApplication } from '@bino/utils/functions/ga';
import { useCartContext } from '../../../context/CartContext';
import WhenCanIApply from '../WhenCanIApply/WhenCanIApply';
import type { Arrangement } from '../../../types';
import { hasProperty, isArrangementInCart } from '../../../utils';
import { addToCart } from '../../../api';
import { ApplyDialog } from '../ApplyDialog/ApplyDialog';
import { useProductPageContext } from '../../../context/ProductPageContext';

function addToCartErrorMessage(error: unknown) {
    return hasProperty(error, 'type') && error.type === 'ItemAlreadyAdded'
        ? 'cart:errorAlreadyExists'
        : 'cart:errorPostingToCart';
}

export type ApplyProps = Omit<ButtonProps, 'onClick'>;

export const Apply = (buttonProps: ApplyProps) => {
    const { t } = useTranslation();
    const [addingToCart, setAddingToCart] = useState<Arrangement['bannerId'] | null>(null);

    const { cart, loadingCart, refreshCart, setCartOpen, disableCart, emergencyContactFormUrl } = useCartContext();
    const { programme, setErrorMessage } = useProductPageContext();

    const canApply = programme.applyItems.some(({ isOpenForApplicants }) => isOpenForApplicants);
    const showEmergencyContactForm = disableCart && emergencyContactFormUrl;

    const handleCartError = (translationKey: string) => {
        setErrorMessage(t(translationKey));
        setAddingToCart(null);
    };

    const addStudyToCart = async (arrangement: Arrangement): Promise<boolean> => {
        if (isArrangementInCart(arrangement.bannerId, cart.items)) {
            handleCartError('cart:errorAlreadyExists');
            return false;
        }

        try {
            setAddingToCart(arrangement.bannerId);
            trackGAEventForApplication('addToCart', [
                {
                    id: String(programme.programmeId),
                    name: programme.name,
                    campus: t(`cart:campus.${arrangement.campus}`),
                },
            ]);
            await addToCart([arrangement]);
            await refreshCart();
            setCartOpen(true);
            setAddingToCart(null);
            return true;
        } catch (error) {
            handleCartError(addToCartErrorMessage(error));
        }
        return false;
    };

    if (!canApply) {
        if (programme.programmeId === null) return null;
        return <WhenCanIApply programmeId={programme.programmeId} {...buttonProps} />;
    }

    if (showEmergencyContactForm) {
        return (
            <Button asChild startIcon={<CartIcon aria-hidden='true' />} {...buttonProps}>
                <a href={emergencyContactFormUrl}>{t('cart:applynow')}</a>
            </Button>
        );
    }

    return (
        <ApplyDialog
            addToCart={addStudyToCart}
            itemBeingAdded={addingToCart}
            programme={programme}
            disabled={loadingCart}
            {...buttonProps}
        />
    );
};
